import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { inr } from '@/lib/tagPricing';
import { tagFigures } from './utils';

const g3 = (n) => Number(n || 0).toFixed(3);
const c2 = (n) => Number(n || 0).toFixed(2);
const RM = [['dia', 'Diamonds'], ['stone', 'Colour stones'], ['pearl', 'Pearls'], ['other', 'Other']];

function Row({ label, value, sub, strong }) {
  return (
    <div className={`flex items-baseline justify-between gap-2 py-1 ${strong ? 'border-t border-border/70 pt-2' : ''}`}>
      <span className={`text-[length:calc(var(--fs,1)*0.71875rem)] ${strong ? 'font-semibold text-foreground' : 'text-muted-foreground'}`}>{label}</span>
      <span className="text-right">
        <span className={`font-mono text-[length:calc(var(--fs,1)*0.75rem)] tabular-nums ${strong ? 'font-bold text-foreground' : 'font-semibold text-foreground'}`}>{value}</span>
        {sub ? <span className="block font-mono text-[length:calc(var(--fs,1)*0.625rem)] tabular-nums text-emerald-700 dark:text-emerald-400">{sub}</span> : null}
      </span>
    </div>
  );
}

// One collapsible summary card (expanded by default).
export function SummaryCard({ title, s, values, grand, testid, pieces = true }) {
  const [open, setOpen] = useState(true);
  const rs = (v) => `Rs. ${inr(v)}`;
  return (
    <div className={`overflow-hidden rounded-2xl border ${grand ? 'border-emerald-700/40 bg-gradient-to-b from-emerald-700/10 to-transparent' : 'border-border/70 bg-background/60'}`} data-testid={testid}>
      <button type="button" onClick={() => setOpen(!open)} aria-expanded={open} className="flex w-full items-center justify-between px-3.5 py-2.5 text-left" data-testid={`${testid}-toggle`}>
        <span>
          <span className={`block text-[length:calc(var(--fs,1)*0.8125rem)] font-semibold ${grand ? 'text-emerald-800 dark:text-emerald-300' : 'text-foreground'}`}>{title}</span>
          <span className="block text-[length:calc(var(--fs,1)*0.65625rem)] uppercase tracking-[0.14em] text-muted-foreground">{s.count} piece{s.count === 1 ? '' : 's'}{values ? ` · ${rs(s.total)}` : ''}</span>
        </span>
        <ChevronDown size={15} className={`text-muted-foreground transition-transform duration-200 ${open ? '' : '-rotate-90'}`} />
      </button>
      {open ? (
        <div className="px-3.5 pb-3 animate-in fade-in slide-in-from-top-1 duration-150">
          <Row label="Gross weight" value={`${g3(s.gross)} g`} />
          <Row label="Net weight" value={`${g3(s.net)} g`} sub={values ? `Gold ${rs(s.gold)}` : ''} />
          {RM.map(([k, label]) => ((k !== 'other' || s[k].ct) ? <Row key={k} label={label} value={`${c2(s[k].ct)} ct${pieces && s[k].pcs ? ` · ${s[k].pcs} pcs` : ''}`} sub={values ? rs(s[k].amt) : ''} /> : null))}
          {values ? <><Row label="Labour" value={rs(s.labour)} /><Row label="Other charges" value={rs(s.charges)} /><Row label="Total value" value={rs(s.total)} strong /></> : null}
        </div>
      ) : null}
    </div>
  );
}

// Purity cards + grand total card.
export function SummaryStack({ summary, values, testid = 'tagws-summary-stack', pieces = true }) {
  const purTitle = (l) => (l === 'No purity' ? 'No purity' : `${l} Gold`);
  return (
    <div className="space-y-2.5" data-testid={testid}>
      <SummaryCard title="Grand total" s={summary.grand} values={values} grand pieces={pieces} testid={`${testid}-grand`} />
      {summary.groups.map((g) => <SummaryCard key={g.label} title={purTitle(g.label)} s={g} values={values} pieces={pieces} testid={`${testid}-${g.label.replace(/\W+/g, '').toLowerCase() || 'none'}`} />)}
    </div>
  );
}

// Weights summary of workspace cards (raw material snapshot), for the stock panel.
export function stockSummary(items) {
  const blank = () => ({ count: 0, gross: 0, net: 0, dia: { ct: 0, pcs: 0 }, stone: { ct: 0, pcs: 0 }, pearl: { ct: 0, pcs: 0 }, other: { ct: 0, pcs: 0 } });
  const K = { diamond: 'dia', color_stone: 'stone', pearl: 'pearl' };
  const by = new Map(); const grand = blank();
  const add = (s, j) => {
    s.count += 1; s.gross += Number(j.gross_weight_grams || 0); const f = tagFigures(j); s.net += f.net;
    f.lines.forEach((m) => { const k = K[m.materialType] || 'other'; s[k].ct += Number(m.carats || 0); s[k].pcs += Number(m.pieces || 0); });
  };
  [...items].sort((a, b) => Number(b.gold_purity_percent || 0) - Number(a.gold_purity_percent || 0)).forEach((j) => {
    const l = j.gold_purity_name || 'No purity';
    if (!by.has(l)) by.set(l, blank());
    add(by.get(l), j); add(grand, j);
  });
  return { groups: [...by.entries()].map(([label, s]) => ({ label, ...s })), grand };
}
